import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";

const EmbroideryWorkspacePage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState({});

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${settings.apiURL}/get_embroidery_orders.php`,
        {
          headers: authHeader(),
        }
      );
      if (resp.data?.success && Array.isArray(resp.data.orders)) {
        setOrders(resp.data.orders);
      } else {
        setOrders([]);
        toast("Embroidery orders not found", "info");
      }
    } catch (err) {
      console.error("Failed to fetch embroidery orders", err);
      toast("Failed to fetch embroidery orders", "error");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredItems = (order) =>
    order.items?.filter(
      (item) =>
        item.name && !["discount", "shipping"].includes(item.name.toLowerCase())
    ) || [];

  const calculateAge = (createDate) => {
    if (!createDate) return 0;
    const now = new Date();
    const orderTime = new Date(createDate);
    const diffInHours = Math.floor((now - orderTime) / (1000 * 60 * 60));
    return diffInHours;
  };

  const getAgeCellStyle = (hours) => {
    if (hours < 24) {
      return { backgroundColor: "#d4edda", color: "#155724" };
    } else if (hours < 48) {
      return { backgroundColor: "#fff3cd", color: "#856404" };
    } else {
      return { backgroundColor: "#f8d7da", color: "#721c24" };
    }
  };

  const handleCreateLabel = async (order) => {
    if (!order.orderId || !order.orderNumber) {
      toast("Invalid order data", "error");
      return;
    }

    setCreatingLabel((prev) => ({ ...prev, [order.orderId]: true }));

    try {
      const resp = await axios.post(
        `${settings.apiURL}/create_shipstation_label.php`,
        {
          orderId: order.orderId,
          orderNumber: order.orderNumber,
        },
        { headers: authHeader() }
      );

      if (resp.data.success) {
        toast("Label created successfully!", "success");

        // PDF'i indir
        if (resp.data.labelUrl) {
          const link = document.createElement("a");
          link.href = settings.apiURL + resp.data.labelUrl;
          link.download = resp.data.labelFile;
          link.click();
        }

        // Order listesini güncelle
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o.orderId === order.orderId ? { ...o, is_placed: true } : o
          )
        );
      } else {
        toast(resp.data.message || "Label creation failed", "error");
      }
    } catch (err) {
      console.error("Label creation error:", err);
      toast("Label creation failed", "error");
    } finally {
      setCreatingLabel((prev) => ({ ...prev, [order.orderId]: false }));
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f7f7",
        padding: "16px",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button
          onClick={logout}
          style={{
            background: "#ff7043",
            color: "#fff",
            border: "none",
            padding: "10px 18px",
            borderRadius: "6px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Logout
        </button>
      </div>

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "10px",
          padding: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <h3 style={{ margin: 0 }}>Embroidery Orders</h3>
          <button
            onClick={fetchOrders}
            className="btn btn-sm btn-primary"
            style={{ minWidth: "100px" }}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : orders.length === 0 ? (
          <p>No embroidery orders found.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total Qty</th>
                  <th>Age</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Internal Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => {
                  const items = filteredItems(order);
                  const totalQty = items.reduce(
                    (sum, item) => sum + (item.quantity || 0),
                    0
                  );
                  const ageHours = calculateAge(order.createDate);

                  return (
                    <tr key={order.orderId || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            color: "#007bff",
                            textDecoration: "underline",
                            cursor: "pointer",
                            padding: 0,
                          }}
                          onClick={() =>
                            navigate(`/embroidery-order/${order.orderId}`, {
                              state: { orders },
                            })
                          }
                        >
                          {order.orderNumber}
                        </button>
                      </td>
                      <td>{order.shipTo?.name || "-"}</td>
                      <td>{items.length}</td>
                      <td>{totalQty}</td>
                      <td
                        style={{
                          ...getAgeCellStyle(ageHours),
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {ageHours} hr
                      </td>
                      <td>
                        {order.createDate
                          ? new Date(order.createDate).toLocaleString()
                          : "-"}
                      </td>
                      <td
                        style={{
                          backgroundColor: order.is_placed
                            ? "#198754"
                            : order.design_uploaded
                            ? "#ffc107"
                            : "#6c757d",
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {order.is_placed
                          ? "Placed"
                          : order.design_uploaded
                          ? "Uploaded"
                          : "Pending"}
                      </td>
                      <td
                        style={{
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={order.internalNotes || "-"}
                      >
                        {order.internalNotes || "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-success me-2"
                          onClick={() =>
                            navigate(`/embroidery-order/${order.orderId}`, {
                              state: { orders },
                            })
                          }
                        >
                          View Details
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleCreateLabel(order)}
                          disabled={
                            creatingLabel[order.orderId] ||
                            order.is_placed ||
                            !order.design_uploaded
                          }
                        >
                          {creatingLabel[order.orderId] ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              ></span>
                              Creating...
                            </>
                          ) : order.is_placed ? (
                            "Label Created"
                          ) : (
                            "Create Label"
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbroideryWorkspacePage;
